import { FieldArray, Formik, useFormikContext } from "formik";
import { useEffect } from "react";
import apiRequest from "../../../utils/admin/apiRequest";
import InputField from "../../ui/form/InputField";
import Button from "../../ui/utils/Button";
import SelectField from "../../ui/form/SelectField";
import usePost from "../../../hooks/api/usePost";
import Alarm from "../../ui/utils/Alert";
import useFetch from "../../../hooks/api/useFetch";
import { updateVendor } from "../../../validation/vendor/authValidation";
import { CgAdd } from "react-icons/cg";
import { Trash } from "lucide-react";

// ----------------------------------------
// LOCATION WATCHER
// ----------------------------------------
const LocationWatcher = ({
  fetchStates,
  fetchCities,
  setBlankResponse,
  setCityBlankResponse,
}) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (values.countryId) {
      fetchStates({ countryId: values.countryId });
      setFieldValue("stateId", "");
    } else {
      setBlankResponse();
    }
  }, [values.countryId]);

  useEffect(() => {
    if (values.stateId) {
      fetchCities({ stateId: values.stateId });
      setFieldValue("cityId", "");
    } else {
      setCityBlankResponse();
    }
  }, [values.stateId]);

  return null;
};

// ----------------------------------------
// CORPORATE WATCHER
// ----------------------------------------
const CorporateWatcher = ({
  values,
  fetchTowerAdmins,
  clearTowerAdmins,
  fetchTowerCodes,
  clearTowerCodes,
}) => {
  useEffect(() => {
    values.corporateCodes.forEach((row) => {
      if (row.adminId) fetchTowerAdmins({ adminId: row.adminId });
      else clearTowerAdmins();
    });
  }, [values.corporateCodes.map((r) => r.adminId).join(",")]);

  useEffect(() => {
    values.corporateCodes.forEach((row) => {
      if (row.corporateId) fetchTowerCodes({ corporateAdminId: row.corporateId });
      else clearTowerCodes();
    });
  }, [values.corporateCodes.map((r) => r.corporateId).join(",")]);

  return null;
};

// ----------------------------------------
// EDIT VENDOR MAIN COMPONENT
// ----------------------------------------
const EditVendor = ({ data, closeModal, callback }) => {
  const { error, isLoading: loading, post, serverErrors } = usePost(
    apiRequest.editVendor
  );


  // LOAD admin list
  const { data: adminList } = useFetch({
    apiMethod: apiRequest.getHeadAdmin,
    body: { paginate: false, sortField: "name", sortOrder: "asc" },
    autoFetch: true,
  });

  // corporate admin list fetcher
  const { data: towerAdmins, refetch: fetchTowerAdmins, setBlankResponse: clearTowerAdmins } =
    useFetch({
      apiMethod: apiRequest.getCorporateAdminByHeadAdmin,
      autoFetch: false,
    });

  // corporate codes fetcher
  const { data: towerCodes, refetch: fetchTowerCodes, setBlankResponse: clearTowerCodes } =
    useFetch({
      apiMethod: apiRequest.getCorporateCodesByCorporateAdmin,
      autoFetch: false,
    });

  // Location Fetch
  const { data: countryData } = useFetch({
    apiMethod: apiRequest.getCountry,
    body: { paginate: false },
  });

  const { data: stateData, refetch: fetchStates, setBlankResponse } = useFetch({
    apiMethod: apiRequest.getStateByCountryId,
    autoFetch: false,
  });

  const {
    data: cityData,
    refetch: fetchCities,
    setBlankResponse: setCityBlankResponse,
  } = useFetch({
    apiMethod: apiRequest.getCityByState,
    autoFetch: false,
  });


  const v = data;

  console.log(v);
  
  // ----------------------------------------
  // INITIAL VALUES FROM DATABASE
  // ----------------------------------------
  const initialValues = {
    name: v.name || "",
    countryId: v?.countryId?._id || "",
    stateId: v.stateId?._id || "",
    cityId: v.cityId?._id || "",
    mobile: v.mobile || "",
    email: v.email || "",
    contactPerson: v.contactPerson || "",
    userId: v.userId || "",
    password: "", // password optional during update
    vendorType: v.vendorType,
    isSuperVendor: v.isSuperVendor,
    addedBy: v.addedBy || null,

    userDetails: v.userDetails?.length ? v.userDetails : [
      { name: "", mobile: "", email: "" },
    ],

    corporateCodes: v.corporateCodes?.length
      ? v.corporateCodes.map((c) => ({
          adminId: c.adminId || "",
          corporateId: c.corporateId || "",
          code: c.code || "",
          superVendorId: v._id,
        }))
      : [
          { adminId: "", corporateId: "", code: "", superVendorId: v._id },
        ],
  };

  

  return (
    <div>
      <h2 className="p-4 text-lg font-semibold">Edit Vendor Profile</h2>
      <div className="border-b" />

      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={updateVendor}
        onSubmit={async (values) => {
           const response = await post({ data: { body: values, id: data?._id } });

          if (response.response) callback?.();
        }}
      >
        {({ values, handleChange, handleBlur, handleSubmit, touched, errors }) => (
          <>
            <LocationWatcher
              fetchStates={fetchStates}
              fetchCities={fetchCities}
              setBlankResponse={setBlankResponse}
              setCityBlankResponse={setCityBlankResponse}
            />

            <CorporateWatcher
              values={values}
              fetchTowerAdmins={fetchTowerAdmins}
              clearTowerAdmins={clearTowerAdmins}
              fetchTowerCodes={fetchTowerCodes}
              clearTowerCodes={clearTowerCodes}
            />

            <form onSubmit={handleSubmit} className="text-sm max-h-[80vh] overflow-auto">
              {error && <Alarm message={error} type="error" />}

              {/* ------------------------------ MAIN GRID START ------------------------------ */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-4">

                <InputField
                  name="name"
                  label="Vendor Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && errors.name}
                  serverError={serverErrors?.name}
                />

                <InputField
                  name="mobile"
                  value={values.mobile}
                  label="Mobile"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.mobile && errors.mobile}
                  serverError={serverErrors?.mobile}
                />

                <InputField
                  name="email"
                  value={values.email}
                  label="Email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={touched.email && errors.email}
                  serverError={serverErrors?.email}
                />

                <SelectField
                  name="countryId"
                  value={values.countryId}
                  label="Country"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={countryData?.countries?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.countryId && errors.countryId}
                  serverError={serverErrors?.countryId}
                />

                <SelectField
                  name="stateId"
                  value={values.stateId}
                  label="State"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={stateData?.states?.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                  error={touched.stateId && errors.stateId}
                  serverError={serverErrors?.stateId}
                />

                <SelectField
                  name="cityId"
                  value={values.cityId}
                  label="City"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  options={cityData?.cities?.map((c) => ({
                    value: c._id,
                    label: c.name,
                  }))}
                  error={touched.cityId && errors.cityId}
                  serverError={serverErrors?.cityId}
                />

                <InputField
                  name="contactPerson"
                  label="Contact Person"
                  value={values.contactPerson}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.contactPerson && errors.contactPerson}
                  serverError={serverErrors?.contactPerson}
                />

                <InputField
                  name="userId"
                  label="User ID"
                  value={values.userId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.userId && errors.userId}
                  serverError={serverErrors?.userId}
                />

                <InputField
                  type="text"
                  name="password"
                  label="Password (optional)"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.password && errors.password}
                  serverError={serverErrors?.password}
                />

             
                <div className="col-span-full">
                  <h3 className="font-semibold mb-2">Partner Details</h3>

                  <FieldArray name="userDetails">
                    {({ push, remove }) => (
                      <>
                        <table className="w-full border">
                          <thead>
                            <tr>
                              <th className="p-2 border">Name</th>
                              <th className="p-2 border">Mobile</th>
                              <th className="p-2 border">Email</th>
                              <th className="p-2 border text-center">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {values.userDetails.map((row, index) => (
                              <tr key={index}>

                                {/* NAME */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.name`}
                                    value={row.name}
                                    placeholder="Enter name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.name &&
                                      errors.userDetails?.[index]?.name
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.name`]}
                                  />
                                </td>

                                {/* MOBILE */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.mobile`}
                                    value={row.mobile}
                                    onChange={handleChange}
                                    placeholder="Enter mobile"

                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.mobile &&
                                      errors.userDetails?.[index]?.mobile
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.mobile`]}
                                  />
                                </td>

                                {/* EMAIL */}
                                <td className="p-2 border">
                                  <InputField
                                    name={`userDetails.${index}.email`}
                                    value={row.email}
                                    onChange={handleChange}
                                    placeholder="Enter email"

                                    onBlur={handleBlur}
                                    error={
                                      touched.userDetails?.[index]?.email &&
                                      errors.userDetails?.[index]?.email
                                    }
                                    serverError={serverErrors?.[`userDetails.${index}.email`]}
                                  />
                                </td>

                                {/* REMOVE BUTTON */}
                                <td className="text-center p-2 border">
                                  <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    className="text-red-500"
                                  >
                                    <Trash size={17} />
                                  </button>
                                </td>

                              </tr>
                            ))}
                          </tbody>
                        </table>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              push({ name: "", mobile: "", email: "" })
                            }
                            className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                          >
                            <CgAdd /> Add User
                          </button>
                        </div>
                      </>
                    )}
                  </FieldArray>

                </div>

                <div className="col-span-full mt-4">
                  <h3 className="font-semibold mb-2">Corporate Codes</h3>

                  <FieldArray name="corporateCodes">
                    {({ push, remove }) => (
                      <>
                        <table className="w-full border">
                          <thead>
                            <tr>
                              <th className="p-2 border">Admin Head</th>
                              <th className="p-2 border">Corporate Admin</th>
                              <th className="p-2 border">Code</th>
                              <th className="p-2 border text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {values.corporateCodes.map((row, index) => {
                              const adminOptions = adminList?.admins?.map((a) => ({
                                value: a._id,
                                label: a.name,
                              }));

                              const corporateAdminOptions =
                                towerAdmins?.admins?.map((a) => ({
                                  value: a._id,
                                  label: a.name,
                                })) || [];

                              // ------------------ UNIQUE CODE FILTER ------------------
                              const selectedCodes = values.corporateCodes
                                .map((r, i) => (i !== index ? r.code : null))
                                .filter(Boolean);

                              const currentAdmin = towerAdmins?.admins?.find(
                                (a) => a._id === row.corporateId
                              );

                              const adminCodes = currentAdmin?.corporateCodes || [];

                              const filteredCodes = adminCodes.filter(
                                (c) => !selectedCodes.includes(c.code)
                              );
                              // ----------------------------------------------------------

                              return (
                                <tr key={index}>
                                  {/* ADMIN HEAD */}
                                  <td className="p-2 border">
                                    <SelectField
                                      name={`corporateCodes.${index}.adminId`}
                                      value={row.adminId}
                                      onChange={(e) => {
                                        handleChange(e);
                                        values.corporateCodes[index].corporateId = "";
                                        values.corporateCodes[index].code = "";
                                      }}
                                      options={adminOptions}
                                    />
                                  </td>

                                  {/* CORPORATE ADMIN */}
                                  <td className="p-2 border">
                                    <SelectField
                                      name={`corporateCodes.${index}.corporateId`}
                                      value={row.corporateId}
                                      onChange={async (e) => {
                                        handleChange(e);
                                        values.corporateCodes[index].code = "";

                                        const res = await fetchTowerCodes({
                                          corporateAdminId: e.target.value,
                                        });

                                        values.corporateCodes[index].codesList =
                                          res?.codes || [];
                                      }}
                                      options={corporateAdminOptions}
                                    />
                                  </td>

                                  {/* CODE DROPDOWN */}
                                  <td className="p-2 border">
                                    <SelectField
                                      name={`corporateCodes.${index}.code`}
                                      value={row.code}
                                      options={filteredCodes.map((c) => ({
                                        value: c.code,
                                        label: c.code,
                                      }))}
                                      onChange={handleChange}
                                    />
                                  </td>

                                  <td className="text-center p-2 border">
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-500"
                                    >
                                      <Trash size={17} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>

                        </table>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() =>
                              push({
                                adminId: "",
                                corporateId: "",
                                superVendorId: null,
                                code: "",
                              })
                            }
                            className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-2"
                          >
                            <CgAdd /> Add Code
                          </button>
                        </div>
                      </>
                    )}
                  </FieldArray>

                </div>

              </div>

              <div className="flex justify-end gap-2 p-4 border-t">
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isLoading={loading}>
                  Update Vendor
                </Button>
              </div>
            </form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default EditVendor;
